import { useState } from 'react';
import { PencilSimpleIcon, PlusIcon, TrashIcon } from '@phosphor-icons/react';
import { Num, Plural, T, useGT } from 'gt-react';
import type {
  WorkbookColumnType,
  WorkbookSheet,
  WorkbookValidation,
} from '@erp/shared-types';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useValidationText } from './useValidationText';
import { WorksheetValidationForm } from './WorksheetValidationForm';
import {
  withValidation,
  withoutValidation,
  type ChangeSheetResult,
  type ValidationState,
} from './worksheetValidations';
import { IconTooltip } from '@/components/common/IconTooltip';

type ColumnType = WorkbookColumnType;

/** How the shown Result fared on one rule, in words and in its status color. */
export function ValidationStatusText({
  validation,
  state,
}: {
  validation: WorkbookValidation;
  state: ValidationState | undefined;
}) {
  if (!state || state.kind === 'unchecked')
    return (
      <span className="text-muted-foreground">
        <T context="worksheet validation status">Not checked yet</T>
      </span>
    );
  if (state.kind === 'changed')
    return (
      <span className="text-muted-foreground">
        <T context="worksheet validation status">Changed since the run</T>
      </span>
    );
  const failed = state.check.failedRowCount;
  if (!failed)
    return (
      <span className="text-green-700 dark:text-green-400">
        <T context="worksheet validation status">Passed</T>
      </span>
    );
  return (
    <span
      className={cn(
        'tabular-nums',
        validation.severity === 'error'
          ? 'text-destructive'
          : 'text-amber-700 dark:text-amber-400',
      )}
    >
      <T context="worksheet validation status: rows failing it">
        <Num>{failed}</Num>{' '}
        <Plural n={failed} one="row fails" other="rows fail" />
      </T>
    </span>
  );
}

/**
 * A column's Validations in its menu: each rule with how the shown Result
 * fared, edited or removed in place, and a form to add one.
 */
export function WorksheetColumnValidations({
  sheet,
  column,
  type,
  states,
  readOnly,
  change,
}: {
  sheet: WorkbookSheet;
  column: string;
  type: ColumnType;
  states: ReadonlyMap<string, ValidationState>;
  readOnly: boolean;
  change: ChangeSheetResult;
}) {
  const gt = useGT();
  const text = useValidationText();
  // The rule being edited, or 'new' while adding one.
  const [editing, setEditing] = useState<string | null>(null);
  const rules = (sheet.validations ?? []).filter(
    (validation) => validation.column === column,
  );
  const save = (validation: WorkbookValidation) => {
    change((latest) => withValidation(latest, validation));
    setEditing(null);
  };
  return (
    <section
      className="flex flex-col gap-1.5 border-t pt-2"
      aria-label={gt('Validations of {column}', {
        context: 'worksheet column validations',
        column,
      })}
    >
      <h5 className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
        <T context="worksheet column validations heading">Validations</T>
      </h5>
      {rules.length > 0 && (
        <ul className="flex flex-col gap-1">
          {rules.map((rule) =>
            editing === rule.id ? (
              <li key={rule.id}>
                <WorksheetValidationForm
                  sheetId={sheet.id}
                  column={column}
                  type={type}
                  initial={rule}
                  onSave={save}
                  onCancel={() => setEditing(null)}
                />
              </li>
            ) : (
              <li
                key={rule.id}
                className="flex items-center gap-2 text-xs"
                aria-label={text.named(rule)}
              >
                <Badge
                  variant={
                    rule.severity === 'error' ? 'destructive' : 'outline'
                  }
                  className={cn(
                    'px-1.5 py-0 text-[11px]',
                    rule.severity === 'warning' &&
                      'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-700 dark:bg-amber-950 dark:text-amber-200',
                  )}
                >
                  {text.severity(rule.severity)}
                </Badge>
                <span
                  className="min-w-0 flex-1 truncate"
                  title={text.describe(rule)}
                >
                  {text.describe(rule)}
                </span>
                <ValidationStatusText
                  validation={rule}
                  state={states.get(rule.id)}
                />
                {!readOnly && (
                  <>
                    <IconTooltip>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-6"
                        aria-label={gt('Edit validation {rule}', {
                          context: 'worksheet validation action',
                          rule: text.describe(rule),
                        })}
                        onClick={() => setEditing(rule.id)}
                      >
                        <PencilSimpleIcon size={14} />
                      </Button>
                    </IconTooltip>
                    <IconTooltip>
                      <Button
                        type="button"
                        size="icon"
                        variant="ghost"
                        className="size-6"
                        aria-label={gt('Remove validation {rule}', {
                          context: 'worksheet validation action',
                          rule: text.describe(rule),
                        })}
                        onClick={() =>
                          change((latest) => withoutValidation(latest, rule.id))
                        }
                      >
                        <TrashIcon size={14} />
                      </Button>
                    </IconTooltip>
                  </>
                )}
              </li>
            ),
          )}
        </ul>
      )}
      {!rules.length && editing !== 'new' && (
        <p className="text-xs text-muted-foreground">
          <T context="worksheet column validations empty">
            Check every row of this column when the workbook runs, not only the
            rows on screen.
          </T>
        </p>
      )}
      {editing === 'new' ? (
        <WorksheetValidationForm
          sheetId={sheet.id}
          column={column}
          type={type}
          onSave={save}
          onCancel={() => setEditing(null)}
        />
      ) : (
        !readOnly && (
          <Button
            type="button"
            size="sm"
            variant="ghost"
            className="self-start"
            onClick={() => setEditing('new')}
          >
            <PlusIcon />
            <T context="worksheet add validation action">Add validation</T>
          </Button>
        )
      )}
    </section>
  );
}
